import { useState, useRef } from "react";
import { Form, Formik } from "formik";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { smsNotificationValidationSchema } from "../../schemas/smsNotification";
import useNotificationService from "../../services/api/notificationService";
import { enums } from "../../constants/enum";

import { Badge, Button, Loading, Tab } from "../../components";
import { Radio, TextArea } from "../../components/forms";
import { CloseIcon, SendIcon } from "../../assets/icons";
import {
  CustomerListTab,
  GroupListTab,
} from "../../components/NotificationTabs";

import "./NotificationsPage.scss";

export default function SentMessages() {
  const { createSmsNotification } = useNotificationService();
  const formikRef = useRef(null);

  const initialValues = {
    to: [],
    group: null,
    smsContentNature: enums.MESSAGE_TYPES.TRANSACTIONAL,
    message: "",
    type: enums.MESSAGE_TYPES.INSTANT,
    scheduleTime: null,
  };

  const [activeContactTab, setActiveContactTab] = useState("Select Contacts");
  const [selectedCustomers, setSelectedCustomers] = useState([]);

  const { isPending, mutate } = useMutation({
    mutationFn: (values) => createSmsNotification({ payload: values }),
    onSuccess: () => {
      toast.success("Message sent successfully");
      setSelectedCustomers([]);
      formikRef.current?.resetForm();
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.payload?.message || enums.DEFAULT_ERROR_MESSAGE,
      );
    },
  });

  const handleAddCustomer = (customer) => {
    // If it's a single customer (object with id)
    if (customer.id) {
      const customerId = customer.id;
      if (!selectedCustomers.find((c) => c.id === customerId)) {
        const newCustomers = [
          ...selectedCustomers,
          { ...customer, id: customerId },
        ];
        setSelectedCustomers(newCustomers);

        // Update Formik state directly
        const currentTo = formikRef.current?.values.to || [];
        formikRef.current?.setFieldValue("to", [...currentTo, customerId]);
      }
    }
  };

  const handleRemoveCustomer = (id) => {
    setSelectedCustomers(selectedCustomers.filter((c) => c.id !== id));

    // Update Formik state directly
    const currentTo = formikRef.current?.values.to || [];
    if (Object.values(enums.VIRTUAL_GROUPS).includes(id)) {
      formikRef.current?.setFieldValue("to", []);
    } else {
      formikRef.current?.setFieldValue(
        "to",
        currentTo.filter((cId) => cId !== id),
      );
    }
  };

  const handleAddGroup = ({
    selectedGroup = "",
    customerIds = [],
    groupId = null,
  }) => {
    // clear selected customers
    setSelectedCustomers([]);

    // set group as selected customer
    setSelectedCustomers((prev) => [
      ...prev,
      { name: selectedGroup, id: groupId ? groupId : selectedGroup },
    ]);
    // set group
    if (groupId) {
      formikRef.current?.setFieldValue("to", []);
      formikRef.current?.setFieldValue("group", groupId);
    } else {
      formikRef.current?.setFieldValue("group", null);
      formikRef.current?.setFieldValue("to", customerIds);
    }
  };

  const handleSubmit = (values) => {
    // console.log(values);
    mutate(values);
  };

  return (
    <div className="notifications-content-grid">
      {/* Left Column: Contact Selection */}
      <div className="contact-selection-panel">
        <Tab
          tabs={["Select Contacts", "Groups"]}
          activeTab={activeContactTab}
          onTabChange={setActiveContactTab}
          className="contact-tabs"
        >
          {activeContactTab === "Select Contacts" && (
            <CustomerListTab onAddCustomer={handleAddCustomer} />
          )}
          {activeContactTab === "Groups" && (
            <GroupListTab
              onSelectGroup={handleAddGroup}
              disabled={selectedCustomers.length > 0}
            />
          )}
        </Tab>
      </div>

      {/* Right Column: Message Composition */}
      <div className="message-composition-panel">
        <Formik
          innerRef={formikRef}
          initialValues={initialValues}
          validationSchema={smsNotificationValidationSchema}
          onSubmit={handleSubmit}
        >
          {({ values }) => (
            <Form>
              <div className="panel-section selected-customers">
                <label>Selected Customers</label>
                <div className="chips-container">
                  {selectedCustomers.length === 0 && (
                    <span className="placeholder-text">
                      No customers selected
                    </span>
                  )}
                  {selectedCustomers.map((c) => (
                    <Badge
                      key={c.id}
                      text={c.name}
                      icon={<CloseIcon className="remove-icon" />}
                      className="customer-chip"
                      onClick={() => handleRemoveCustomer(c.id)}
                    />
                  ))}
                </div>
              </div>

              <div className="panel-section message-type">
                <div className="radio-group">
                  <label className="radio-label">
                    <Radio
                      name="smsContentNature"
                      checked={
                        values.smsContentNature ===
                        enums.MESSAGE_TYPES.TRANSACTIONAL
                      }
                      value={enums.MESSAGE_TYPES.TRANSACTIONAL}
                    />
                    <span>Transactional Message</span>
                  </label>
                  <label className="radio-label">
                    <Radio
                      name="smsContentNature"
                      checked={
                        values.smsContentNature ===
                        enums.MESSAGE_TYPES.PROMOTIONAL
                      }
                      value={enums.MESSAGE_TYPES.PROMOTIONAL}
                    />
                    <span>Promotional Message</span>
                  </label>
                </div>
              </div>

              <div className="panel-section message-body">
                <TextArea
                  label="Message"
                  placeholder="Type here"
                  name="message"
                  maxLength={1500}
                />
              </div>

              <div className="info-footer">
                <p>Note:</p>
                <ul>
                  <li>
                    A SMS page has a maximum character limit of 160 characters.
                  </li>
                  <li>
                    A SMS message can have a maximum size of 1500 characters.
                  </li>
                  <li>
                    The <strong>cost of a SMS page is 0.59 LKR.</strong>
                  </li>
                  <li>
                    The cost per SMS page is multiplied by the number of SMS
                    pages, which is the cost for one recipient. The total cost
                    is the product of the cost per recipient and the number of
                    recipients.
                  </li>
                </ul>
              </div>

              <Button
                text="Send"
                type="submit"
                className="action-btn"
                icon={<SendIcon />}
                disabled={isPending}
              />
            </Form>
          )}
        </Formik>
      </div>
      <Loading variant="fullScreen" isLoading={isPending} />
    </div>
  );
}
